/**
 * Alpine component: profileOccupations
 *
 * Gestisce le professioni/discipline del profilo (max 3) + situazione lavorativa.
 * API: GET /v1/occupations, GET /v1/occupations/:id/disciplines, GET /v1/work-situations
 * PATCH /v1/users/me con disciplines[] + work_condition
 */
export function registerProfileOccupations(Alpine) {
    Alpine.data('profileOccupations', (userData) => ({
        // Discipline correnti (array di oggetti)
        disciplines: [],
        _originalDisciplines: [],
        workCondition: String(userData.work_condition || ''),
        _originalWorkCondition: '',

        // Dati API
        occupations: [],
        workSituations: [],
        discOptions: {}, // cache discipline per occupation_id
        loadingOccupations: false,
        loadingWorkSituations: false,

        errors: {},
        saving: false,
        saved: false,

        async init() {
            // Inizializza discipline dal profilo utente
            this.disciplines = (userData.disciplines || []).map(d => ({
                occupation_id: String(d.occupation_id || ''),
                discipline_id: String(d.discipline_id || ''),
                order: d.order || '',
                order_num: d.order_num || '',
                order_num_required: d.order_num_required || false,
                _loadingDisc: false,
            }));

            // Se non ha discipline, mostra un form vuoto
            if (this.disciplines.length === 0) {
                this.disciplines.push(this._emptyDiscipline());
            }

            this._originalDisciplines = JSON.parse(JSON.stringify(this.disciplines));
            this._originalWorkCondition = this.workCondition;

            // Carica dati API
            await Promise.all([
                this._loadOccupations(),
                this._loadWorkSituations(),
            ]);

            // Pre-carica discipline per le occupation già selezionate
            for (const disc of this.disciplines) {
                if (disc.occupation_id) {
                    await this._loadDisciplines(disc.occupation_id);
                }
            }
        },

        _emptyDiscipline() {
            return {
                occupation_id: '',
                discipline_id: '',
                order: '',
                order_num: '',
                order_num_required: false,
                _loadingDisc: false,
            };
        },

        addDiscipline() {
            if (this.disciplines.length >= 3) return;
            this.disciplines.push(this._emptyDiscipline());
        },

        removeDiscipline(index) {
            this.disciplines.splice(index, 1);
        },

        async onOccupationChange(index) {
            const disc = this.disciplines[index];
            disc.discipline_id = '';
            disc.order = '';
            disc.order_num = '';

            if (!disc.occupation_id) {
                disc.order_num_required = false;
                return;
            }

            // Verifica se order_num è required
            const occ = this.occupations.find(o => String(o.id) === String(disc.occupation_id));
            disc.order_num_required = occ?.order_num_required ?? false;

            await this._loadDisciplines(disc.occupation_id);
        },

        reset() {
            this.disciplines = JSON.parse(JSON.stringify(this._originalDisciplines));
            this.workCondition = this._originalWorkCondition;
            this.errors = {};
            this.saved = false;
        },

        async save() {
            this.errors = {};
            this.saved = false;
            this.saving = true;

            // Validazione client-side
            let valid = true;
            this.disciplines.forEach((disc, i) => {
                if (!disc.occupation_id) { this.errors['occupation_' + i] = 'Seleziona una professione'; valid = false; }
                if (!disc.discipline_id) { this.errors['discipline_' + i] = 'Seleziona una disciplina'; valid = false; }
                if (disc.order_num_required) {
                    if (!disc.order?.trim()) { this.errors['order_' + i] = 'Campo obbligatorio'; valid = false; }
                    if (!disc.order_num?.trim()) { this.errors['order_num_' + i] = 'Campo obbligatorio'; valid = false; }
                }
            });

            if (!valid) {
                this.saving = false;
                return;
            }

            try {
                const body = {
                    disciplines: this.disciplines
                        .filter(d => d.occupation_id && d.discipline_id)
                        .map(d => ({
                            occupation_id: parseInt(d.occupation_id),
                            discipline_id: parseInt(d.discipline_id),
                            order: d.order || '',
                            order_num: d.order_num || '',
                        })),
                };

                if (this.workCondition) {
                    body.work_condition = parseInt(this.workCondition);
                }

                await this._apiPatch('/v1/users/me', body);

                this._originalDisciplines = JSON.parse(JSON.stringify(this.disciplines));
                this._originalWorkCondition = this.workCondition;
                this.saved = true;
                setTimeout(() => { this.saved = false; }, 3000);
            } catch (err) {
                if (err.validation_messages) {
                    for (const [field, msgs] of Object.entries(err.validation_messages)) {
                        this.errors[field] = Object.values(msgs)[0] || 'Errore';
                    }
                } else {
                    this.errors._global = err.message || 'Errore di rete';
                }
            } finally {
                this.saving = false;
            }
        },

        // ── API loaders ────────────────────────────────────────────
        async _loadOccupations() {
            if (this.occupations.length) return;
            this.loadingOccupations = true;
            try {
                const res = await fetch(`${window.IKOS.apiBase}/v1/occupations`);
                const data = await res.json();
                this.occupations = data._embedded?.items ?? [];
            } catch {}
            this.loadingOccupations = false;
        },

        async _loadWorkSituations() {
            if (this.workSituations.length) return;
            this.loadingWorkSituations = true;
            try {
                const res = await fetch(`${window.IKOS.apiBase}/v1/work-situations`);
                const data = await res.json();
                this.workSituations = data._embedded?.items ?? [];
            } catch {}
            this.loadingWorkSituations = false;
        },

        async _loadDisciplines(occupationId) {
            if (this.discOptions[occupationId]) return;

            // Mark loading on relevant disciplines
            this.disciplines.forEach(d => {
                if (String(d.occupation_id) === String(occupationId)) d._loadingDisc = true;
            });

            try {
                const res = await fetch(`${window.IKOS.apiBase}/v1/occupations/${occupationId}/disciplines?page_size=-1`);
                const data = await res.json();
                this.discOptions[occupationId] = data._embedded?.items ?? [];
            } catch {
                this.discOptions[occupationId] = [];
            }

            this.disciplines.forEach(d => {
                if (String(d.occupation_id) === String(occupationId)) d._loadingDisc = false;
            });
        },

        async _apiPatch(path, body) {
            const apiBase = window.IKOS?.apiBase || '';
            const headers = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };
            const authToken = window.IKOS?.authToken;
            if (authToken && authToken !== true) headers['Auth-Token'] = authToken;
            const guestId = window.IKOS?.guestId;
            if (guestId) headers['X-Guest-Id'] = guestId;

            const res = await fetch(apiBase + path, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok) throw data;
            return data;
        },
    }));
}
